/**
 * PlacementReconciler — PLAT-10921 step 4 (epic decision E7).
 *
 * Re-derives every **pinned** composition's timeline position from its current
 * reference, after any change that could have moved that reference. Generalized
 * out of `EndCardManager`, which did exactly this for one hardcoded case;
 * `EndCardManager` now delegates here.
 *
 * **THE TIMELINE IS THE DRIVER.** When the reference moves, pinned graphics
 * follow. Nothing here ever resists a creator's timeline edit — an edge the
 * placement doesn't bind is left alone, and a `selection` edge that was moved
 * onto new words was rebound at the edit site (see `placementOverrides.ts`), so
 * by the time this runs the anchor already points where the creator put it.
 *
 * Only `time` and `duration` are ever written. Canvas position, scale and box
 * size are a different axis and can never trigger a reconcile — a longer
 * generated hook changes the canvas, never the timeline.
 *
 * ## Ordering (DS3) — read before adding a fourth pass
 *
 * `AbstractCommand.recalculateTimeline` runs the model-mutating passes in this
 * order, and each depends on the ones before it:
 *
 *   1. **Karaoke track** (`KaraokeTrackManager.reconcileKaraokeTrack`)
 *   2. **Punch list** (`PunchListTrackManager.reconcilePunchList`)
 *   3. **Placements — THIS pass**
 *   4. `applyDefaultAudioFades`, then `timeline.recalculateDuration()`
 *
 * **Coupled logos are NOT in that list.** Coupling is applied at
 * *serialization* — `Timeline.adjustCoupledElementsToTrackBounds` clones and
 * adjusts, and `reapplyCoupledElementBounds` pushes the result back into the
 * model afterwards. So at reconcile time a coupled logo's `time` / `duration`
 * are a value from the *previous* serialization. That is why
 * `getContentEndTime` skips them, and it is the undeclared dependency this
 * comment exists to declare. Folding coupled logos into this pass is a separate
 * refactor (deliberately out of scope).
 *
 * **Inside this pass, end cards settle first.** A `clip`-placed graphic's scope
 * end IS the end card's `time`, so phase A reconciles end cards against the
 * content end and phase B reconciles everything else against the now-settled
 * scope. One pass, two phases, in that order.
 */

import { readPlacementMeta } from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { resolveAnchorTimeRange } from '@src/utility/placementAnchor';

import { CompositionElement } from '../models/elements/CompositionElement';
import {
  derivePinnedTimelineTarget,
  findEndCardIds,
  findPinnedFollowerIds,
  getContentEndTime,
  resolveTimelineScopeBounds,
} from './placementBounds';
import {
  evaluatePlacementEdge,
  isPinnablePlacement,
  PLACEMENT,
} from './placementEvaluation';

import type { PlacementEdge } from './placementEvaluation';

import type { VideoEditor } from '../VideoEditor';
import type { PinnedTimelineTarget } from './placementBounds';
import type { Video } from '@src/types.ts/story';
import type { PlacementMeta } from '@src/utility/extraElementData';
import type {
  AnchorTranscriptElement,
  TranscriptAnchor,
} from '@src/utility/placementAnchor';

const logger = LogManager.getLogger('editor:PlacementReconciler');

/** Sub-frame differences aren't worth a model write (or an undo-less mutation). */
const EPSILON = 0.001;

export class PlacementReconciler {
  private editor: VideoEditor;

  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Re-derives every pinned composition. Safe to call repeatedly: each
   * derivation reads only the current timeline (or transcript), so a second run
   * with nothing changed writes nothing.
   */
  public reconcile(): void {
    const extraElementData = this.editor.getTrackManager().extraElementDataRaw;
    if (!extraElementData) return;

    const pinned = this.findPinnedCompositions(extraElementData);
    if (pinned.length === 0) return;

    const { tracks } = this.editor.timeline;
    const endCardIds = findEndCardIds(tracks, extraElementData);
    // Every pinned graphic is a FOLLOWER and must not count toward the content
    // end that defines its own bounds — see `findPinnedFollowerIds` for the
    // circular dependency this breaks.
    const followerIds = findPinnedFollowerIds(tracks, extraElementData);

    // Phase A then phase B — see the ordering note above. `clip` reads the end
    // card's settled `time` as its scope end, so end cards go first.
    const phaseA = pinned.filter(p => endCardIds.has(p.element.id));
    const phaseB = pinned.filter(p => !endCardIds.has(p.element.id));

    logger.debug('Reconciling placements', {
      endCards: phaseA.length,
      others: phaseB.length,
    });

    // Built at most ONCE per pass, and only if a `selection` graphic is
    // actually pinned — `getFullTranscript` is O(all words) and uncached, and
    // this pass runs on every command. Same thunk the edit sites use
    // (`TimelineStore.recordDraggedPlacementOverrides`).
    let transcript: readonly AnchorTranscriptElement[] | undefined;
    const getTranscript = () =>
      (transcript ??= this.editor.getFullTranscript());

    for (const entry of [...phaseA, ...phaseB]) {
      this.reconcileOne(
        entry,
        extraElementData,
        endCardIds,
        followerIds,
        getTranscript,
      );
    }
  }

  private reconcileOne(
    { element, meta }: PinnedComposition,
    extraElementData: NonNullable<Video['extraElementData']>,
    endCardIds: ReadonlySet<string>,
    followerIds: ReadonlySet<string>,
    getTranscript: () => readonly AnchorTranscriptElement[],
  ): void {
    const target =
      meta.placement === PLACEMENT.SELECTION
        ? this.deriveSelectionTarget(
            element.id,
            extraElementData,
            getTranscript,
          )
        : this.deriveScopeTarget(
            element.id,
            meta,
            extraElementData,
            endCardIds,
            followerIds,
          );

    if (!target) return;

    // Only the edges this placement actually binds are written. An `accept` edge
    // holds whatever the creator left there. (`pinned: true` because
    // `findPinnedCompositions` already filtered on it.)
    const binds = (edge: PlacementEdge) =>
      evaluatePlacementEdge({
        placement: meta.placement,
        edge,
        pinned: true,
      }) !== 'accept';

    const startTarget =
      target.time !== null && binds('start') ? target.time : null;
    const endTarget =
      target.endTime !== null && binds('end') ? target.endTime : null;
    if (startTarget === null && endTarget === null) return;

    const nextTime =
      startTarget !== null ? Math.max(0, startTarget) : element.time;
    // With the start free, the end is absorbed by `duration` so `time` survives.
    const nextDuration =
      endTarget !== null
        ? Math.max(0, endTarget - nextTime)
        : element.durationSeconds;

    if (nextDuration <= 0) {
      // A collapsed range would make the graphic un-selectable on the timeline.
      // Leave it where it is and say so rather than writing a zero duration.
      logger.warn('Skipping placement reconcile — derived range is empty', {
        elementId: element.id,
        placement: meta.placement,
        nextTime,
        nextDuration,
      });
      return;
    }

    if (
      Math.abs(element.time - nextTime) < EPSILON &&
      Math.abs(element.durationSeconds - nextDuration) < EPSILON
    ) {
      return;
    }

    logger.debug('Repositioning pinned composition', {
      elementId: element.id,
      placement: meta.placement,
      from: { time: element.time, duration: element.durationSeconds },
      to: { time: nextTime, duration: nextDuration },
    });

    // Direct `update` rather than a command: this is a derived sync, not a user
    // action. It must not enter the undo stack — the same contract
    // `reconcileEndCards` / `reapplyCoupledElementBounds` already work under.
    // The caller (`recalculateTimeline`) owns the markDirty that follows.
    element.update(
      endTarget !== null
        ? { time: nextTime, duration: nextDuration }
        : { time: nextTime },
    );
  }

  /**
   * `clip` / `start` / `end` — derived from the card-bounded scope.
   *
   * End cards, every pinned follower, and the element itself are all excluded
   * from the content end: a graphic whose own end runs past the content would
   * otherwise define the very bound it is about to be snapped to.
   */
  private deriveScopeTarget(
    elementId: string,
    meta: PlacementMeta,
    extraElementData: NonNullable<Video['extraElementData']>,
    endCardIds: ReadonlySet<string>,
    followerIds: ReadonlySet<string>,
  ): PinnedTimelineTarget | null {
    if (!meta.placement) return null;
    const { tracks } = this.editor.timeline;
    // Pre-computed once per pass by `reconcile` (the sets are the same for every
    // element), plus this element itself.
    const excluded = new Set([...endCardIds, ...followerIds, elementId]);

    return derivePinnedTimelineTarget({
      placement: meta.placement,
      compositionType: meta.compositionType,
      scope: resolveTimelineScopeBounds(tracks, extraElementData, excluded),
      contentEndTime: getContentEndTime(tracks, excluded),
      overlapBefore: meta.overlapBefore,
    });
  }

  /**
   * `selection` — derived from the anchored WORDS, never from the scope (E4).
   *
   * This is also why `selection` needs no separate transcript-edit trigger: the
   * derivation reads the live transcript, so whichever change ran the pass, the
   * range lands on the current position of the same words. A content cut inside
   * the passage shrinks the range and a restore grows it back, with no override
   * recorded — `findOverlayRanges` snaps endpoints inward to surviving words and
   * keeps deleted words bracketed by survivors inside the contiguous range.
   */
  private deriveSelectionTarget(
    elementId: string,
    extraElementData: NonNullable<Video['extraElementData']>,
    getTranscript: () => readonly AnchorTranscriptElement[],
  ): PinnedTimelineTarget | null {
    const anchor = readTranscriptAnchor(extraElementData[elementId]);
    // Read BEFORE the transcript is built: an unanchored graphic must not pay
    // for a transcript it never consults.
    if (!anchor) return null;

    const range = resolveAnchorTimeRange(getTranscript(), anchor);
    // The whole passage was cut. Leave the graphic alone — collapsing it onto
    // the cut point is worse than letting it sit where it is.
    if (!range) return null;

    return { time: range.time, endTime: range.endTime };
  }

  private findPinnedCompositions(
    extraElementData: NonNullable<Video['extraElementData']>,
  ): PinnedComposition[] {
    const pinned: PinnedComposition[] = [];

    for (const track of this.editor.timeline.tracks) {
      for (const element of track.elements) {
        if (!(element instanceof CompositionElement)) continue;
        const meta = readPlacementMeta(extraElementData[element.id]);
        // Absence of `placementPinned` means NOT pinned (E11): a legacy record
        // has no flag and no enforcement, and handing those to this loop would
        // silently move graphics in published stories.
        if (!meta?.pinned || !isPinnablePlacement(meta.placement)) continue;
        pinned.push({ element, meta });
      }
    }

    return pinned;
  }
}

interface PinnedComposition {
  element: CompositionElement;
  meta: PlacementMeta;
}

function readTranscriptAnchor(meta: unknown): TranscriptAnchor | null {
  if (!meta || typeof meta !== 'object') return null;
  const anchor = (meta as Record<string, unknown>).transcriptAnchor;
  if (!anchor || typeof anchor !== 'object') return null;
  const { startInitialIndex, endInitialIndex } = anchor as Record<
    string,
    unknown
  >;
  if (
    typeof startInitialIndex !== 'number' ||
    typeof endInitialIndex !== 'number'
  ) {
    return null;
  }
  return { startInitialIndex, endInitialIndex };
}
